import React, { useState } from 'react';
import {
  tableHeaderCellClass, tableHeaderRowClass,
} from 'features/activity/styles/dataTableStyles';
import { primaryBtn, neutralBtn, textBtn } from 'features/settings/components/SettingsRows';

/**
 * Shared vocabulary for the two halves of Ask credits.
 *
 * The card used to be one component on the Usage page doing two unrelated
 * jobs: moving money and explaining where it went. They live on separate
 * pages now — buying is Billing, watching is Usage — and these are the pieces
 * both still need to agree on. A balance formatted one way on Billing and
 * another on Usage reads as two different numbers.
 */

/**
 * Dollars for anything a person would recognise as money.
 *
 * Six decimals is not fussiness: a common Ask call costs about $0.008, so
 * rounding to a cent either shows nothing or overstates it by a quarter. The
 * precision scales with the magnitude instead — a balance reads as a balance,
 * a single model call reads as the fraction of a cent it is.
 */
export function usd(n) {
  const v = Number(n) || 0;
  if (v === 0) return '$0.00';
  if (v < 0.01) return `$${v.toFixed(6)}`;
  if (v < 1) return `$${v.toFixed(4)}`;
  return `$${v.toFixed(2)}`;
}

export function when(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

export const LEDGER_LABEL = {
  grant: 'Plan credit granted',
  expire: 'Unused plan credit lapsed',
  purchase: 'Credit purchased',
  charge: 'Lucid usage',
  refund: 'Refunded',
  release: 'Hold released',
  reserve: 'Held for a research run',
  settle: 'Run settled',
};

/**
 * The ledger kinds that are money moving IN or OUT of the account, as opposed
 * to the running cost of using it.
 *
 * Billing shows these: grants, purchases, lapses, refunds — the handful of
 * events a person would expect to reconcile against a card statement. The
 * `charge` rows are excluded there on purpose; one question can write several,
 * so a busy week buries the one purchase the reader came to find. Usage shows
 * charges instead, grouped by run, which is the form in which they answer
 * anything.
 */
export const MONEY_KINDS = new Set(['grant', 'purchase', 'expire', 'refund']);

/* ── Shared presentation for Usage and Billing ───────────────────────────────
 * Sections are a semibold title over a hairline; values are plain rows; tables
 * match Traces. Nothing here draws a filled panel or a card inside a card.
 */

export const INK = 'var(--text-primary)';
export const MUTED = 'var(--text-muted)';

export const btnPrimary = primaryBtn;
export const btnNeutral = `${neutralBtn} whitespace-nowrap`;
export const linkBtn = textBtn;

export function Section({ title, action = null, children }) {
  return (
    <section className="mb-8">
      <div className="mb-3 flex min-h-[32px] items-center justify-between gap-3 border-b border-[color:var(--border-default)] pb-2">
        <h3 className="text-[13px] font-semibold tracking-tight" style={{ color: INK }}>{title}</h3>
        {action}
      </div>
      {children}
    </section>
  );
}

/** Plain key/value row on a hairline. */
export function KV({ label, children }) {
  return (
    <div className="flex min-h-[36px] items-center justify-between gap-4 border-b border-[color:var(--border-subtle)] py-1.5 last:border-b-0">
      <span className="text-[12px]" style={{ color: MUTED }}>{label}</span>
      <span className="min-w-0 text-right text-[12px] tabular-nums" style={{ color: INK }}>{children}</span>
    </div>
  );
}

/** A figure with its label under it. No box. */
export function Stat({ label, value, big = false }) {
  return (
    <div className="min-w-0">
      <div
        className={`${big ? 'text-[26px]' : 'text-[16px]'} font-[450] leading-none tabular-nums`}
        style={{ color: INK }}
      >
        {value}
      </div>
      <div className="mt-1.5 text-[11px]" style={{ color: MUTED }}>{label}</div>
    </div>
  );
}

export const tableShell = 'overflow-x-auto rounded-md border border-[color:var(--border-default)] bg-surface';
export const th = tableHeaderCellClass;
export const thRow = tableHeaderRowClass;
export const trBody = 'h-8 border-b border-[color:var(--border-subtle)] last:border-b-0 hover:bg-surface-hover transition-colors';
export const td = 'px-3 text-left align-middle text-[12px] text-[color:var(--text-primary)]';

export function Pill({ label, color, bg }) {
  return (
    <span
      className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium whitespace-nowrap"
      style={{ color, backgroundColor: bg }}
    >
      {label}
    </span>
  );
}

/**
 * Daily bars in the RunsHistogram style: no background, no y axis, a baseline,
 * first and last date under it, and a tooltip that follows the bar.
 *   data   [{ key, label, value }]
 */
export function DailyBars({ data, format = (v) => String(v), height = 72, color = 'var(--trodo-primary)' }) {
  const [tip, setTip] = useState(null);
  const max = Math.max(...data.map((d) => d.value), 0.000001);
  return (
    <div className="relative select-none" style={{ fontFamily: 'Inter, sans-serif' }}>
      <div className="flex items-end gap-[2px] border-b border-[color:var(--border-default)]" style={{ height }}>
        {data.map((d) => (
          <div
            key={d.key}
            className="group/bar flex h-full flex-1 cursor-default items-end"
            onMouseEnter={(e) => {
              const r = e.currentTarget.getBoundingClientRect();
              setTip({ x: r.left + r.width / 2, y: r.top, d });
            }}
            onMouseLeave={() => setTip(null)}
          >
            {d.value > 0 ? (
              <div
                className="w-full opacity-70 transition-opacity group-hover/bar:opacity-100"
                style={{ height: `${Math.max(4, (d.value / max) * 100)}%`, backgroundColor: color, borderRadius: '2px 2px 0 0' }}
              />
            ) : null}
          </div>
        ))}
      </div>
      <div className="mt-1 flex justify-between text-[10px]" style={{ color: MUTED }}>
        <span>{data[0]?.label}</span>
        <span>{data[data.length - 1]?.label}</span>
      </div>
      {tip ? (
        <div
          className="pointer-events-none fixed z-[100] rounded-lg border border-[color:var(--border-default)] bg-[color:var(--surface-raised)] px-3 py-2 shadow-lg"
          style={{ left: tip.x, top: Math.max(tip.y - 8, 8), transform: 'translate(-50%, -100%)' }}
        >
          <div className="text-[11px]" style={{ color: MUTED }}>{tip.d.label}</div>
          <div className="text-[12px] font-medium tabular-nums" style={{ color: INK }}>{format(tip.d.value)}</div>
        </div>
      ) : null}
    </div>
  );
}
